/*eslint-disable @typescript-eslint/no-unused-vars*/
/*eslint-disable @typescript-eslint/ban-types */
import React, { useCallback, useMemo, useState } from 'react'
import { IDerivedCondition } from './DerivedConditionPlan'
import { SidePopover, useAppDispatch, useAppSelector, useEffectOnce } from '../../../globalUtils/globalExports'
import { Box, IconButton, Paper, Typography } from '@mui/material'
import { Icon } from '../../../globalUtils/globalIcons'
import EditIcon from '@mui/icons-material/Edit';
import DerivedConditionSidePopover, { isOperator } from './DerivedConditionSidepopover'
import { fetchAdvanceRuleEngineArithmeticOperatorsReferenceData, fetchAdvanceRuleEngineRelationalOperatorReferenceData, getAdvanceRuleEngineReducer, getReferenceDataReducer } from '../../../rmsReduxStore/reduxExports'
import { isInteger } from 'lodash'
import { ICustomCondition } from './CustomConditionComponent'


interface CustomNodeProps {
    data: {
        derivedCondition: IDerivedCondition
        handleUpdateDerivedCondition: Function
    }
}

const DerivedConditionNode: React.FC<CustomNodeProps> = (props: CustomNodeProps) => {
    const dispatch = useAppDispatch();
    const { derivedCondition, handleUpdateDerivedCondition } = props.data;
    const [isSidePopoverOpen, setIsSidePopoverOpen] = useState<boolean>(false);
    const { derivedConditionFormRedux } = useAppSelector(getAdvanceRuleEngineReducer);
    const { advanceRuleEngineArithmeticOperatorReferenceData, advanceRuleEngineRelationalOperatorReferenceData } = useAppSelector(getReferenceDataReducer);

    const handleCloseSidePopover = () => {
        setIsSidePopoverOpen(false);
    }

    const handleEditClick = () => {
        setIsSidePopoverOpen(true);
    }

    const operatorRef = useMemo(() => {
        const arithmeticOperators = advanceRuleEngineArithmeticOperatorReferenceData ?? [];
        const relationalOperators = advanceRuleEngineRelationalOperatorReferenceData ?? [];
        return [...relationalOperators, ...arithmeticOperators];
    }, [advanceRuleEngineArithmeticOperatorReferenceData, advanceRuleEngineRelationalOperatorReferenceData])


    const getCondition = (label: string) => {

        const condition: ICustomCondition = derivedCondition?.conditions?.find(item => item.condName === label) as ICustomCondition;

        const value = condition?.arithmeticParamId ? <>
            <span className='trigger-condition-bold'>&quot;{condition?.arithmeticParamLabel}&quot;</span>
            <span className='trigger-condition-normal'>{condition?.arithmeticOperator}</span>
            <span className='trigger-condition-bold'>{condition?.arithmeticParamValue}</span>
            {condition?.arithmeticParamUnit && <span className='trigger-condition-bold'>{condition?.arithmeticParamUnit}</span>}
        </>
            : condition?.isParamTypeEnum ? <span className='trigger-condition-bold'>{'('}{condition?.paramEnumValue?.join(', ')}{')'}</span>
                : condition?.isParamTypeDate && isInteger(Number(condition?.parameter1Value)) ? <span className='trigger-condition-bold'>{'( Last '}{condition?.parameter1Value}{' days )'}</span>
                    : condition?.parameter2Value ?
                        <>
                            <span className='trigger-condition-bold'> {'('} {condition?.parameter1Value} , {condition?.parameter2Value} {')'} </span>
                            {condition?.paramUnit && <span className='trigger-condition-bold'>{condition?.paramUnit}</span>}
                        </>
                        : <>
                            <span className='trigger-condition-bold'>{condition?.parameter1Value}</span>
                            {condition?.paramUnit && <span className='trigger-condition-bold'>{condition?.paramUnit}</span>}
                        </>



        return <div className='trigger-condition-container'>
            <span className='trigger-condition-normal'>{'('}</span>
            <span className='trigger-condition-bold'>&quot;{condition?.paramLabel}&quot;</span>
            <span className='trigger-condition-normal'>{operatorRef?.find(item => item?.id === condition?.operator)?.label}</span>
            {value}
            <span className='trigger-condition-normal'>{')'}</span>
        </div>
    }

    const parseExpression = useCallback(() => {
        const tokens = derivedCondition?.expression?.split(' ');
        const updatedTokens = tokens?.map(token => {
            return isOperator(token) ? <span className='trigger-condition-normal'>{token}</span> : getCondition(token);
        })
        return updatedTokens;
    }, [derivedCondition, operatorRef])

    useEffectOnce(() => {
        dispatch(fetchAdvanceRuleEngineArithmeticOperatorsReferenceData());
        dispatch(fetchAdvanceRuleEngineRelationalOperatorReferenceData());
    })

    return (
        <>
            <Paper
                elevation={3}
                sx={{
                    border: '1px solid #F0BB0F',
                    borderLeft: '6px solid #F0BB0F',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    minWidth: '400px !important',
                    maxWidth: '600px !important',
                    backgroundColor: '#fff',
                    position: 'relative',
                    marginBottom: '16px',
                    minHeight: '150px'
                }}
            >
                {/* Derived Condition Title */}
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    marginBottom="8px"
                >
                    <Box display="flex" alignItems="center">
                        <Icon iconClassName={'set-parameter-sidebar'} />
                        <Typography
                            variant="subtitle1"
                            sx={{
                                fontWeight: 500,
                                fontSize: '0.9rem',
                                color: '#002126'
                            }}
                        >
                            {'Derived Condition'}
                        </Typography>
                    </Box>
                </Box>

                {/* Derived Condition body */}
                <Box
                    sx={{
                        backgroundColor: '#FFFBEF',
                        padding: '8px',
                        borderRadius: '4px',
                        position: 'relative',
                        minHeight: '30px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            color: '#333',
                        }}
                    >
                        {
                            derivedCondition?.conditions && derivedCondition?.conditions?.length > 0 ? (
                                <div className='advance__rule__engine__custom-condition'>{parseExpression()}</div>
                            )
                                : (
                                    // Fallback content when there's no condition
                                    <div>
                                        ((Parameter),(Operator),(Value))
                                    </div>
                                )}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={handleEditClick}
                        sx={{
                            ':hover': {
                                backgroundColor: 'transparent', // Removes background hover effect
                            },
                        }}

                    >
                        <EditIcon fontSize="small" sx={{ color: '#666' }} />
                    </IconButton>
                </Box>
            </Paper>
            {
                isSidePopoverOpen && <SidePopover
                    id='rule__engine__execution__plan__if__then__else__side__popOver'
                    open={isSidePopoverOpen}
                    headerText={'Derived Condition'}
                    headerSecondaryText={derivedConditionFormRedux?.name}
                    containerClassName='configure__notification__detail__popover'
                    handleClose={() => { }}
                    popperClassName='advance__rule__engine__trigger__point__side__popover'
                    headerClassName={'advance__rule__engine__header__config'}
                    horizontalLinePopover={true}
                    isRuleEnginePopOver={true}
                    headingIconClassName={'set-parameter-sidebar'}
                    ruleEngineSidePopoverOnclose={handleCloseSidePopover}
                    infoTitle={''}
                    // eslint-disable-next-line react/no-children-prop
                    children={
                        <DerivedConditionSidePopover
                            derivedCondition={derivedCondition}
                            handleUpdateDerivedCondition={handleUpdateDerivedCondition}
                            handleClose={handleCloseSidePopover}
                        />
                    }
                />
            }
        </>
    )
}

export default DerivedConditionNode